import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import {
  Checkmark,
  Close,
  Edit,
  Delete,
} from '@telus-uds/palette-allium/build/rn/icons';
import {
  IconButton,
  Table,
  Typography,
  Skeleton,
  Responsive,
  Box,
} from '@telus-uds/components-web';

import useStore from '../../../../../store/store';
import { deleteRequest, putRequest } from '../../../../../config/apiClient';

export default function ProjectListRow({ projectData }) {
  const { id, name, canDeleted } = projectData;

  const { t } = useTranslation(['adminControl']);
  const { fetchProjects, setProjectMessage } = useStore((state) => state);

  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [projectName, setProjectName] = useState(name);

  const getSection = () => {
    if (loading) return <Skeleton characters={6} />;
    if (isEdit)
      return (
        <div className="edit-box-main">
          <input
            className={`${!projectName ? 'error' : ''}`}
            value={projectName}
            onChange={({ target: { value } }) => setProjectName(value)}
            type="text"
          />
          {!projectName && (
            <Typography variant={{ colour: 'danger' }}>
              {t('nameErrorLabel')}
            </Typography>
          )}
        </div>
      );
    if (isDelete)
      return (
        <Box variant={{ background: 'warning' }} space={4}>
          <Typography>{t('deleteConfirmationLabel')}</Typography>
          <Typography variant={{ bold: true }}>{projectName} ?</Typography>
        </Box>
      );
    return <Typography>{projectName}</Typography>;
  };

  const getAction = () => {
    if (loading) return <Skeleton characters={2} />;
    if (isEdit || isDelete)
      return (
        <div className="action-items-main">
          <div className="action-item">
            <IconButton
              icon={Checkmark}
              action="accept"
              variant={{ raised: true }}
              onPress={onConfirm}
            />
          </div>
          <div className="action-item">
            <IconButton
              icon={Close}
              action="cancel"
              variant={{ raised: true }}
              onPress={onCancel}
            />
          </div>
        </div>
      );

    return (
      <div className="action-items-main">
        <div className="action-item">
          <IconButton
            icon={Edit}
            action="edit"
            variant={{ raised: true }}
            onPress={() => setIsEdit(true)}
          />
        </div>
        {canDeleted && (
          <div className="action-item">
            <IconButton
              icon={Delete}
              action="delete"
              variant={{ raised: true }}
              onPress={() => setIsDelete(true)}
            />
          </div>
        )}
      </div>
    );
  };

  const onConfirm = async () => {
    if (isEdit && !projectName) return;
    setLoading(true);
    setIsEdit(false);
    setIsDelete(false);
    try {
      let result;
      if (isEdit) {
        const newPayload = { ...projectData, name: projectName };
        result = await putRequest(`project/${id}`, newPayload);
      }
      if (isDelete) {
        result = await deleteRequest(`project/${id}`);
      }
      if (result)
        setProjectMessage({
          active: true,
          type: 'success',
          message: `Project ${isEdit ? 'updated' : 'deleted'} successfully`,
        });
    } catch (error) {
      console.log('Error' + error);
    } finally {
      setLoading(false);
      fetchProjects();
    }
  };

  const onCancel = () => {
    if (isEdit) {
      setIsEdit(false);
      setProjectName(name);
    }
    if (isDelete) setIsDelete(false);
  };

  return (
    <Table.Row key={id}>
      {/* For small screens hide project id cell */}
      <Responsive min="md" max="xl">
        <Table.Cell type="rowHeading">#{id}</Table.Cell>
      </Responsive>
      <Table.Cell>{getSection()}</Table.Cell>
      <Table.Cell>{getAction()}</Table.Cell>
    </Table.Row>
  );
}
